import { useEffect, useState, useCallback } from 'react';
import { Stack, useRouter, SplashScreen, Link } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StyleSheet, View, Pressable, Text, Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAtomValue } from 'jotai';
import { useFonts } from 'expo-font';
import { AuthProvider } from '../contexts/AuthContext';
import { ThemeProvider, useTheme } from '../contexts/ThemeContext';
import { DrawerProvider, useDrawer } from '../contexts/DrawerContext';
import { VideoProvider } from '../contexts/VideoContext';
import AuthBottomSheet from '../components/AuthBottomSheet';
import CreateZoneBottomSheet from '../components/CreateZoneBottomSheet';
import CreatePostBottomSheet from '../components/CreatePostBottomSheet';
import HyzoneMascot from '../components/HyzoneMascot';
import MobileDrawer from '../components/MobileDrawer';
import { headerSubtitleAtom } from '../atoms';
import { USE_CUSTOM_FONT, CUSTOM_FONT_FAMILY, LOGO_FONT_FAMILY, HEADER_LOGO_FONT_FAMILY } from '../config/fonts';
import '../i18n';

// Keep splash screen visible while loading fonts (only on native)
if (Platform.OS !== 'web') {
  SplashScreen.preventAutoHideAsync();
}

// Custom header component with logo
function CustomHeader({ showBack = false, showMenuButton = false }: { showBack?: boolean; showMenuButton?: boolean }) {
  const { colors, isDark } = useTheme();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { openDrawer } = useDrawer();

  // On web, use the same left-aligned layout as the tabs header
  if (Platform.OS === 'web') {
    return (
      <View style={[
        {
          backgroundColor: isDark ? colors.background : '#212A35',
          paddingTop: insets.top + 15,
          paddingHorizontal: 16,
          paddingBottom: 16,
          flexDirection: 'row',
          alignItems: 'center',
        }
      ]}>
        <Link href="/" asChild>
          <Pressable style={({ pressed }) => ({ opacity: pressed ? 0.8 : 1 })}>
            <View style={styles.logoContainer}>
              <HyzoneMascot width={34} height={34} />
              <View style={styles.logoTextContainer}>
                <Text style={[
                  styles.logoSmall,
                  {
                    fontFamily: HEADER_LOGO_FONT_FAMILY,
                    fontSize: 24,
                    color: '#85c363',
                    marginTop: 2,
                  }
                ]}>HY</Text>
                <Text style={[
                  styles.logoSmall,
                  {
                    fontFamily: HEADER_LOGO_FONT_FAMILY,
                    fontSize: 24,
                    color: '#85c363',
                    marginTop: 2,
                  }
                ]}>ZONE</Text>
              </View>
            </View>
          </Pressable>
        </Link>
      </View>
    );
  }

  // Mobile layout with back button
  return (
    <View style={[
      styles.customHeader,
      {
        backgroundColor: isDark ? colors.background : '#212A35',
        paddingTop: insets.top,
      }
    ]}>
      {/* Back button or Menu button */}
      <View style={styles.headerSide}>
        {showBack ? (
          <Pressable onPress={() => router.back()} style={styles.backButton}>
            <Ionicons name="chevron-back" size={28} color={colors.text} />
          </Pressable>
        ) : showMenuButton ? (
          <Pressable onPress={openDrawer} style={styles.backButton}>
            <Ionicons name="menu" size={28} color={colors.text} />
          </Pressable>
        ) : null}
      </View>

      {/* Logo - centered */}
      <View style={styles.headerCenter}>
        <View style={styles.logoContainer}>
          <HyzoneMascot width={34} height={34} />
          <View style={styles.logoTextContainer}>
            <Text style={[
              styles.logoSmall,
              {
                fontFamily: HEADER_LOGO_FONT_FAMILY,
                fontSize: 20,
                color: '#85c363', // Light blue color in post details
              }
            ]}>HY</Text>
            <Text style={[
              styles.logoSmall,
              {
                fontFamily: HEADER_LOGO_FONT_FAMILY,
                fontSize: 20,
                color: '#85c363',
              }
            ]}>ZONE</Text>
          </View>
        </View>
      </View>

      {/* Empty right side for balance */}
      <View style={styles.headerSide} />
    </View>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes - reduced refetching
      gcTime: 1000 * 60 * 10, // 10 minutes garbage collection
      retry: 2,
      refetchOnWindowFocus: false, // Prevent refetching when switching tabs
    },
  },
});

function RootLayoutContent() {
  const { colors, isDark } = useTheme();

  return (
    <GestureHandlerRootView style={[styles.container, { backgroundColor: colors.background }]}>
      <AuthProvider>
        <MobileDrawer>
          <Stack
            screenOptions={{
              headerStyle: { backgroundColor: isDark ? colors.background : '#212A35' }, // Dark navy blue header in light theme
              headerTintColor: colors.text,
              headerShadowVisible: false,
            }}
          >
            <Stack.Screen
              name="(tabs)"
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="post/[id]"
              options={{
                header: () => <CustomHeader showBack />,
              }}
            />
            <Stack.Screen
              name="zone/[id]"
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="groupSelector"
              options={{
                presentation: 'modal',
                header: () => <CustomHeader />,
              }}
            />
            <Stack.Screen
              name="page/[slug]"
              options={{
                header: () => <CustomHeader showBack />,
              }}
            />
            <Stack.Screen
              name="[...slug]"
              options={{
                header: () => <CustomHeader showBack />,
              }}
            />
          </Stack>
        </MobileDrawer>
        <AuthBottomSheet />
        <CreateZoneBottomSheet />
        <CreatePostBottomSheet />
        <StatusBar style={isDark ? 'light' : 'dark'} />
      </AuthProvider>
    </GestureHandlerRootView>
  );
}

export default function RootLayout() {
  // Always load the custom font even if USE_CUSTOM_FONT is false, 
  // so it can be used selectively (e.g., for the title)
  const [fontsLoaded, fontError] = useFonts({
    [CUSTOM_FONT_FAMILY]: require('../../assets/fonts/Minecraftia-Regular.ttf'),
    [LOGO_FONT_FAMILY]: require('../../assets/fonts/secondFont.ttf'),
    [HEADER_LOGO_FONT_FAMILY]: require('../../assets/fonts/kongtext.ttf')
  });

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded && Platform.OS !== 'web') {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);

  useEffect(() => {
    if (fontsLoaded) {
      onLayoutRootView();
    }
  }, [fontsLoaded, onLayoutRootView]);

  // On web, don't block rendering if fonts fail or are loading
  if (Platform.OS === 'web' && fontError) {
    console.warn('Font loading error:', fontError);
  }

  // Only block rendering on native platforms
  if (!fontsLoaded && Platform.OS !== 'web') {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <DrawerProvider>
          <VideoProvider>
            <RootLayoutContent />
          </VideoProvider>
        </DrawerProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  customHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    paddingBottom: 10,
  },
  headerSide: {
    width: 44,
    alignItems: 'flex-start',
  },
  headerCenter: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  backButton: {
    padding: 8,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  logoTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoSmall: {
    fontSize: 20,
    fontWeight: '600',
    paddingBottom: 6,
  },
  headerDivider: {
    fontSize: Platform.OS === 'web' ? 20 : 16,
    fontWeight: '300',
    paddingBottom: 6,
  },
  headerSubtitle: {
    fontSize: Platform.OS === 'web' ? 18 : 14,
    fontWeight: '600',
    maxWidth: 200,
    paddingBottom: 6,
  },
});
